import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { allManga } from '@/mocks/manga';
import SEOHead from '@/components/base/SEOHead';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const manga = allManga.find((m) => m.id === id);

  if (!manga) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-file-search-line text-6xl text-foreground-300 mb-4" />
            <p className="text-foreground-500 text-lg">找不到该漫画</p>
            <Link to="/" className="mt-4 inline-block text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedManga = allManga
    .filter((m) => m.id !== manga.id && (m.categorySlug === manga.categorySlug || m.tags.some((t) => manga.tags.includes(t))))
    .slice(0, 4);

  const canonicalUrl = `http://zaixinjiaju.com/article/${manga.id}`;
  const seoData = {
    title: `${manga.title}_${manga.category}_韩漫在线 - 韩国漫画免费阅读`,
    description: `${manga.title} - ${manga.description.slice(0, 120)}`,
    keywords: `${manga.title},${manga.category},${manga.tags.join(',')},韩漫在线,韩国漫画,免费漫画`,
    canonicalUrl,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': [
          { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'http://zaixinjiaju.com/' },
          { '@type': 'ListItem', 'position': 2, 'name': manga.category, 'item': `http://zaixinjiaju.com/category/${manga.categorySlug}` },
          { '@type': 'ListItem', 'position': 3, 'name': manga.title, 'item': canonicalUrl },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        'headline': `${manga.title} - 韩漫在线`,
        'description': manga.description,
        'author': { '@type': 'Person', 'name': manga.author },
        'datePublished': '2026-01-01',
        'dateModified': manga.chapters[0]?.date || '2026-07-03',
        'publisher': { '@id': 'http://zaixinjiaju.com/#organization' },
        'image': manga.cover,
        'mainEntityOfPage': {
          '@type': 'WebPage',
          '@id': `${canonicalUrl}#webpage`,
          'isPartOf': { '@id': 'http://zaixinjiaju.com/#website' },
        },
        'articleSection': manga.category,
        'keywords': manga.tags.join(','),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ImageObject',
        'url': manga.cover,
        'name': `${manga.title} - 封面`,
        'description': `${manga.title}韩国漫画封面 - ${manga.category}`,
      },
    ],
  };

  const breadcrumbs = [
    { label: '首页', href: '/' },
    { label: manga.category, href: `/category/${manga.categorySlug}` },
    { label: manga.title, href: `/article/${manga.id}` },
  ];

  return (
    <>
      <SEOHead {...seoData} />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
            {breadcrumbs.map((crumb, idx) => (
              <span key={crumb.href} className="flex items-center gap-2">
                {idx > 0 && <span className="text-foreground-300">/</span>}
                {idx < breadcrumbs.length - 1 ? (
                  <Link to={crumb.href} className="hover:text-primary-600 transition-colors whitespace-nowrap">
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-foreground-950 font-medium whitespace-nowrap">{crumb.label}</span>
                )}
              </span>
            ))}
          </nav>

          <div className="flex flex-col md:flex-row gap-8 md:gap-10">
            <div className="md:w-72 flex-shrink-0">
              <div className="sticky top-24">
                <div className="w-full aspect-[3/4] rounded-xl overflow-hidden mb-4">
                  <img
                    src={manga.cover}
                    alt={manga.title}
                    title={`${manga.title} - ${manga.author} - 韩漫在线`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <Link
                  to="/category/all"
                  className="block w-full py-3 bg-foreground-950 text-background-50 rounded-full text-sm font-medium text-center hover:bg-foreground-800 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  开始阅读
                </Link>
              </div>
            </div>

            <article className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                {manga.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${
                  manga.status === '连载中'
                    ? 'bg-accent-100 text-accent-700'
                    : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {manga.status}
                </span>
                <span className="text-xs text-foreground-500 whitespace-nowrap">
                  <i className="ri-user-line mr-1" />{manga.author}
                </span>
                <span className="flex items-center gap-0.5 text-sm text-primary-600 font-semibold whitespace-nowrap">
                  <i className="ri-star-fill" />{manga.rating}
                </span>
                <span className="text-xs text-foreground-500 whitespace-nowrap">
                  <i className="ri-eye-line mr-1" />{manga.views} 次阅读
                </span>
                <span className="text-xs text-foreground-500 whitespace-nowrap">
                  共 {manga.totalChapters} 话
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mb-5">
                {manga.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/category/${manga.categorySlug}`}
                    className="px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {tag}
                  </Link>
                ))}
              </div>

              <div className="bg-background-100 rounded-xl p-5 mb-8">
                <h2 className="text-sm font-semibold text-foreground-950 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {manga.description}
                </p>
              </div>

              <section>
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">
                  章节列表
                </h2>
                <div className="bg-background-100 rounded-xl divide-y divide-background-200/70">
                  {manga.chapters.map((ch) => (
                    <Link
                      key={ch.no}
                      to="/category/all"
                      className="flex items-center justify-between p-4 hover:bg-background-50 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-foreground-950 whitespace-nowrap">
                          第{ch.no}话
                        </span>
                        <span className="text-sm text-foreground-600 truncate whitespace-nowrap">
                          {ch.title}
                        </span>
                      </div>
                      <span className="text-xs text-foreground-400 whitespace-nowrap flex-shrink-0 ml-3">
                        {ch.date}
                      </span>
                    </Link>
                  ))}
                </div>
              </section>
            </article>
          </div>

          {relatedManga.length > 0 && (
            <section className="mt-16">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {relatedManga.map((rm) => (
                  <Link
                    key={rm.id}
                    to="/category/all"
                    className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className="p-3">
                      <div className="w-full aspect-[3/4] overflow-hidden rounded-lg mb-3">
                        <img
                          src={rm.cover}
                          alt={rm.title}
                          title={`${rm.title} - 韩漫在线推荐`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                        />
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-950 truncate whitespace-nowrap">
                        {rm.title}
                      </h3>
                      <span className="text-xs text-foreground-500 whitespace-nowrap">
                        {rm.category} · {rm.rating}分
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}